import {
  type Column,
  type ColumnDef,
  EMPTY_ARRAY,
  formattedUTCDay,
  getAgGridColId,
  isPricingModeRelative,
  ModeEnum,
  negotiationStatusMakerLabel,
  type PriceParams,
  type QuoteStatusColumnParams,
  type TextColumnParams,
  useDefaultColumns,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { ManualRFQ } from './types';
import { useManualRFQActionColumn, type UseManualRFQActionColumnParams } from './useManualRFQActionColumn';

type UseManualRFQsBlotterColumnsParams = {
  defaultColumns?: (keyof ManualRFQ | Partial<Column>)[];
} & UseManualRFQActionColumnParams;

export function useManualRFQsBlotterColumns({
  defaultColumns = EMPTY_ARRAY,
  onFixClick,
  onQuoteClick,
  onShowQuoteClick,
}: UseManualRFQsBlotterColumnsParams): Column[] {
  const actionColumn = useManualRFQActionColumn({ onFixClick, onQuoteClick, onShowQuoteClick });

  const columns = useMemo(
    () =>
      compact([
        {
          field: 'SubmitTime',
          type: 'date',
          sortable: true,
          initialSort: '-',
        },
        {
          field: 'QuoteStatus',
          type: 'quoteStatus',
          initialWidth: 120,
          params: {
            tooltipField: 'statusText', // InternalText ?? Text
            rfqProtocolField: 'RFQProtocol',
          } satisfies QuoteStatusColumnParams<ManualRFQ>,
        },
        { field: 'Counterparty', type: 'customer', title: 'Customer' },
        { field: 'MarketAccount', type: 'text', title: 'Account' },
        { field: 'ClearingMarket', type: 'market', title: 'Clearing Market', initialHide: true },
        { field: 'ClearingAccount', type: 'marketAccount', title: 'Clearing Account', initialHide: true },
        { field: 'RFQID', type: 'id', initialWidth: 60 },
        { field: 'tradedSideOrSide', type: 'quoteSide', title: 'Side', id: 'Side' },
        { field: 'symbolOrSymbols', id: 'Symbol', title: 'Symbol', type: 'security' },
        { field: 'OrderQty', type: 'size', params: { currencyField: 'Currency' } },
        { field: 'TradedQty', type: 'size', params: { currencyField: 'Currency' } },
        { field: 'TradedPx', type: 'price', params: { assetField: 'Symbol' } },
        { field: 'TradedAmt', type: 'size', params: { currencyField: 'AmountCurrency' } },
        { field: 'QuoteID', type: 'id', initialWidth: 60 },
        {
          type: 'date',
          field: 'SettleDate',
          title: 'Settlement Date',
          params: { customFormatter: formattedUTCDay },
        },
        {
          field: 'BidPx',
          type: 'price',
          params: {
            assetField: 'Symbol',
            isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          } satisfies PriceParams<ManualRFQ>,
        },
        {
          field: 'bidQtyOrOrderQty',
          title: 'Bid Qty',
          type: 'size',
          params: { currencyField: 'Currency' },
        },
        { field: 'BidAmt', type: 'size', params: { currencyField: 'AmountCurrency' }, initialHide: true },
        {
          field: 'OfferPx',
          type: 'price',
          params: {
            assetField: 'Symbol',
            isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          } satisfies PriceParams<ManualRFQ>,
        },
        {
          field: 'offerQtyOrOrderQty',
          title: 'Offer Qty',
          type: 'size',
          params: { currencyField: 'Currency' },
        },
        { field: 'OfferAmt', type: 'size', params: { currencyField: 'AmountCurrency' }, initialHide: true },
        {
          field: 'fixingIndices',
          title: 'Fixing Index',
          type: 'fixingIndices',
          id: 'fixingIndices',
          initialWidth: 140,
        },
        {
          field: 'FixingDetails',
          title: 'Fixing Price',
          type: 'fixingPrice',
          id: 'fixingPrice',
        },
        {
          field: 'fixingDate',
          title: 'Fixing Date',
          type: 'dateOnly',
          id: 'fixingDate',
        },
        {
          field: 'NegotiationStatus',
          title: 'Negotiation',
          type: 'text',
          initialWidth: 150,
          params: {
            getLabel: negotiationStatusMakerLabel,
          } satisfies TextColumnParams,
          description: 'Shows if the client has requested any changes to the quote. Empty signifies no open requests.',
          initialHide: true,
        },
        {
          id: 'sizeCountering',
          field: 'NegotiationParameters.SizeCountering',
          title: 'Size Countering',
          type: 'text',
          description: 'Shows if Size Countering is enabled',
          params: {
            getLabel: value => (value === ModeEnum.Enabled ? 'Enabled' : ''),
          } satisfies TextColumnParams,
        },
        { field: 'User', type: 'user', initialHide: true },
        { field: 'Timestamp', type: 'date', initialHide: true },
        { field: 'QuoteReqID', type: 'id', initialHide: true },
        { field: 'EndTime', type: 'date', initialHide: true },
        { field: 'Text', type: 'text', initialHide: true },
        { field: 'Parameters', type: 'text', initialHide: true },

        { field: 'ValidUntilTime', type: 'date', initialHide: true },
        { field: 'CustomerUser', title: 'Customer User', type: 'text', initialHide: true },
        { field: 'EffectiveBidSpread', type: 'text', initialHide: true },
        { field: 'PricingMode', type: 'text', title: 'Pricing Mode', initialHide: true },
        { field: 'EffectiveOfferSpread', type: 'text', initialHide: true },
        { field: 'OrigQuoteReqID', type: 'id', initialHide: true },
        { field: 'InternalText', type: 'text', initialHide: true },
        {
          field: 'PricingReference',
          type: 'price',
          params: {
            isReferencePrice: true,
          },
          initialHide: true,
        },
        { field: 'ExternalGroup', type: 'text', title: 'External ID', initialHide: true },
        { field: 'ExternalComments', type: 'text', title: 'External Comments', initialHide: true },
        {
          field: 'postTradeClearingSymbol',
          type: 'text',
          title: 'Post Trade Clearing Symbol',
          initialHide: true,
        },
        {
          field: 'postTradeClearingID',
          type: 'id',
          title: 'Post Trade Clearing ID',
          initialHide: true,
        },
        actionColumn,
      ] satisfies (ColumnDef<ManualRFQ> | false)[]),
    [actionColumn]
  );

  const columnDefinitions = useMemo(() => new Map(columns.map(c => [getAgGridColId(c), c])), [columns]);
  return useDefaultColumns(defaultColumns, columnDefinitions);
}
